import React, { useState, useEffect, useCallback, createContext, useContext, useMemo } from 'react';

import './personalisation-typedefs.js';
import { personalisationData } from '../gateway/personalisationData.js';
import { addToCart } from '../gateway/addToCart.js';
import { baseProductConfigurationSlotId, baseProductConfigurationStepId } from '../utilities/strings.js';
import {
  setProductContext,
  trackPersonalisationStepComplete,
  trackVariantSelected,
  trackSlotPopulated,
  trackSlotRemoved,
  trackPersonalisationComplete
} from '../utilities/tracking.js';
import { calculateFinalPriceModifier } from '../utilities/priceModifier';
import {
  useCacheFilteredPersonalisationStepsAndSlots,
  useHideSteps,
  hideSlotSelections,
  setAllHiddenToFalse
} from '../utilities/hideSlotsAndSteps.js';
import { wait } from '../utilities/wait.js';
import { hasPersonalisationOptions as checkHasPersonalisationOptions } from '../utilities/hasPersonalisationOptions.js';
import { processBaseAttributes } from '../utilities/processLineItems.js';
import { useVisualisationUrlManagement } from '../utilities/useVisualisationUrlManagement.js';
import { buildChoicesFromSelections } from '../utilities/buildChoicesFromSelections.js';
import { cropMaskedImageWithPadding } from '../utilities/cropMaskedImageWithPadding.js';
import { logError, logInfo, logWarning } from '../utilities/logging.js';

/** @typedef {import('./personalisation-typedefs.js').Product} Product */
/** @typedef {import('./personalisation-typedefs.js').Selections} Selections */
/** @typedef {import('./personalisation-typedefs.js').SelectionSlot} SelectionSlot */

// Create the context
export const PersonalisationContext = createContext({
  currentStepId: '',
  setCurrentStepId: /** @type {function(string): void} */ (() => {}),
  currentSlotId: 0,
  setCurrentSlotId: /** @type {function(number): void} */ (() => {}),
  personalisation: null,
  personalisationSteps: new Map(),
  personalisationSlots: new Map(),
  selectedVariantId: null,
  setSelectedVariantId: /** @type {function(any): void} */ (() => {}),
  variants: new Map(),
  addonGroups: new Map(),
  addonItems: new Map(),
  componentGroups: new Map(),
  loading: false,
  isAddingToCart: false,
  isRetrying: false,
  /** @type {Selections} */
  selections: {
    productVariantId: null,
    slots: []
  },
  /** @type {function(Selections): void} */
  setSelections: () => {},
  product: /** @type {Product | null} */ (null),
  currentTotalPrice: 0,
  setCurrentTotalPrice: /** @type {function(number): void} */ (() => {}),
  /** @type {function({ slotId: any, itemSelected: any, groupLineItemPropertyKey?: any, extraImages?: any, supportingImage?: any }):void} */
  updateSelections: () => {},
  /** @type {function({ country: string, onSuccess?: function }):void} */
  updateTheCart: () => {},
  /**
   * Reorders the slot values in selections based on a new order of slot IDs.
   * @param {object} props - The component props.
   * @param {string[]} props.newOrder - Array of slot IDs in the new order.
   */
  reorderSlotValues: () => {},
  setStart: /** @type {function(boolean): void} */ (() => {}),
  personalisationModalOpen: false,
  setPersonalisationModalOpen: /** @type {function(boolean): void} */ (() => {}),
  start: false,
  /** @type {function():void} */
  resetJourney: () => {},
  isResetting: false,
  personalisationId: null,
  /** @type {"FIXED" | "SEQUENTIAL"} */
  visualiserSlotMode: 'FIXED',
  /** @type {{ url: string, visualiserSlotMode: string, groupId: string, file: File | null }[]} */
  visualiserImageUrl: [],
  /** @type {Map<string, { label: string; templateId: string; visualisationType: string, supportingImageLineItemPropertyKey: string }>} */
  visualisationGroups: new Map(),
  lastModifiedGroupId: null,
  hasPersonalisationOptions: false
});

/**
 * Provides personalisation context to children.
 * @param {{ children: any }} props
 * @returns {JSX.Element}
 */
export const PersonalisationProvider = ({ children }) => {
  const [personalisationModalOpen, setPersonalisationModalOpen] = useState(false);
  // Current state
  const [currentStepId, setCurrentStepId] = useState('');
  const [previousStepId, setPreviousStepId] = useState('');
  const [currentSlotId, setCurrentSlotId] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [currentTotalPrice, setCurrentTotalPrice] = useState(0);
  const [selections, setSelections] = useState(
    /** @type {Selections} */ ({
      productVariantId: null,
      slots: []
    })
  );

  // Variants
  const [variants, setVariants] = useState(new Map());

  // Personalisation ui (populated via Storefront API)
  const [personalisation, setPersonalisation] = useState(null);
  const [personalisationSteps, setPersonalisationSteps] = useState(new Map());
  const [personalisationSlots, setPersonalisationSlots] = useState(new Map());
  const [addonGroups, setAddonGroups] = useState(new Map());
  const [addonItems, setAddonItems] = useState(new Map());
  const [componentGroups, setComponentGroups] = useState(new Map());
  const [loading, setLoading] = useState(/** @type {boolean} */ (false));
  const [dataLoaded, setDataLoaded] = useState(/** @type {boolean} */ (false));
  const [product, setProduct] = useState(/** @type {Product | null} */ (null));
  const [start, setStart] = useState(false);
  const [hideSlotsOnVariantsSettings, setHideSlotsOnVariantsSettings] = useState(new Map());
  const [isResetting, setIsResetting] = useState(false);
  const [visualiserSlotMode, setVisualiserSlotMode] = useState('FIXED');
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [hasPersonalisationOptions, setHasPersonalisationOptions] = useState(false);
  const [visualisationGroups, setVisualisationGroups] = useState(new Map());
  const [lastModifiedGroupId, setLastModifiedGroupId] = useState(null);

  useEffect(() => {
    if (!window?.personalisation?.product?.id) {
      throw new Error(
        'Personalisation product ID is not set. Please set the VITE_PERSONALISATION_PRODUCT_ID environment variable.'
      );
    }
    // Grab the product from the window object.
    // @ts-expect-error - This is an external object, so we can't type it.
    const productData = window.personalisation.product;
    setProduct(productData);
    // Set the product context for tracking.
    setProductContext(productData);
  }, []);

  useEffect(() => {
    if (product && !loading && currentStepId !== previousStepId) {
      const stepNumber = Array.from(personalisationSteps.keys()).indexOf(currentStepId);
      trackPersonalisationStepComplete({
        stepNumber,
        stepName: currentStepId,
        totalSteps: personalisationSteps.size
      });
      setPreviousStepId(currentStepId);
    }
  }, [currentStepId, loading, product, personalisationSteps, previousStepId]);

  useEffect(() => {
    if (start && !dataLoaded) {
      async function fetchAll() {
        setLoading(true);
        const countryCode = window.Shopify?.country || 'GB';

        if (product === null) {
          // Bail out if we don't have a product yet.
          return;
        }

        if (!product.id) {
          throw new Error('No product ID found in personalisation');
        }

        if (!countryCode) {
          throw new Error('No country code found in personalisation');
        }

        const {
          personalisationDevModel,
          stepsDevModel,
          slotsDevModel,
          addonGroupsDevModel,
          addonItemsDevModel,
          componentGroupsDevModel,
          variantsDevModel,
          hideSlotsOnVariantsSettings
        } = await personalisationData({ productId: product.id, country: countryCode });

        setVariants(variantsDevModel);
        setHideSlotsOnVariantsSettings(hideSlotsOnVariantsSettings);
        const firstStepId = Array.from(stepsDevModel.keys())[0];
        if (variantsDevModel.size === 1) {
          // @TODO: Check why this doesn't have the extra config - what is this?
          setPersonalisationSteps(stepsDevModel);
          // Ensure currentStepId is set to the first step if not already set and steps exist.
          if (!currentStepId && stepsDevModel.size > 0) {
            setCurrentStepId(firstStepId);
          }
          setSelectedVariantId(variantsDevModel.entries().next().value.id);
        } else {
          setPersonalisationSteps(stepsDevModel);

          // set to the first step
          setCurrentStepId(firstStepId);
          setPreviousStepId(firstStepId);
        }

        setHasPersonalisationOptions(
          checkHasPersonalisationOptions({
            stepsDevModel,
            slotsDevModel,
            componentGroupsDevModel,
            variantsDevModel
          })
        );

        setPersonalisation(personalisationDevModel);
        setPersonalisationSlots(slotsDevModel);
        setAddonGroups(addonGroupsDevModel);
        setAddonItems(addonItemsDevModel);
        setComponentGroups(componentGroupsDevModel);

        const hasBaseProductConfiguration = slotsDevModel.get(baseProductConfigurationSlotId);
        const slotValues = Array.from(slotsDevModel.values());

        // Legacy visualisation slot mode detection.
        setVisualiserSlotMode(slotValues.some((slot) => slot.slotMode === 'SEQUENTIAL') ? 'SEQUENTIAL' : 'FIXED');

        // Set up the selections object for later use.
        setSelections((prev) => ({
          ...prev,
          // Check if the base product configuration slot exists and if not, set the
          // product variant id to the first variant as there should only be one.
          productVariantId: hasBaseProductConfiguration ? null : product.variants[0].id,

          slots: slotValues.map(({ ...slot }) => ({
            ...slot,
            selected: null,
            componentGroupId: null,
            hidden: false,
            supportingImageLineItemPropertyKey: slot.visualisationGroup?.supportingImageLineItemPropertyKey || null
          }))
        }));
        setLoading(false);
        setPersonalisationModalOpen(true);
        setDataLoaded(true);

        // If there's only one variant, we don't need to track the variant selected event.
        if (variantsDevModel.size > 1) {
          const {
            id: variantId,
            price: variantPrice,
            title: variantTitle
          } = variantsDevModel.entries().next().value.at(-1);
          trackVariantSelected({
            variantId,
            variantPrice: Number(variantPrice.amount),
            variantTitle
          });
        }
      }
      fetchAll();
    }
  }, [product, start, currentStepId, dataLoaded]);

  // Keep the total price up to date.
  useEffect(() => {
    // Base price of product (value should be an integer in pence).
    const selectedBasePrice = selections.slots.find(({ id }) => id === baseProductConfigurationSlotId)?.selected;
    const baseProductVariant = variants.get(selectedBasePrice);

    let baseProductPrice = null;
    // If defined
    // Get base product variant
    // Get base product variant price
    // Add base product variant price to total price.
    if (baseProductVariant?.price?.amount) {
      baseProductPrice =
        (typeof baseProductVariant.price.amount === 'string'
          ? Number(baseProductVariant.price.amount)
          : baseProductVariant.price.amount) * 100;
    } else {
      baseProductPrice = typeof product?.price === 'number' ? product.price : 0;
    }

    /*
     * Personalisation Upsells
     * Value from storefront API is a string & in pound format, convert to a integer and then convert to pence for accurate calculation.
     */
    const slotUpsellTotalPrice = selections.slots
      .filter(({ selected, id }) => selected !== null && id !== baseProductConfigurationSlotId)
      .reduce((acc, { selected, id }) => {
        const theSlot = personalisationSlots.get(id);
        // Handle both single selections and arrays for upsells.
        if (Array.isArray(selected)) {
          // For upsells with multiple selections, sum all selected variants.
          return selected.reduce((slotAcc, variantId) => {
            const item = variants.get(variantId);
            if (!item) return slotAcc;

            // Find the component group that contains this variant to get price modifier.
            let priceModifierType = null;
            let priceModifierValue = null;

            for (const [, componentGroup] of componentGroups.entries()) {
              if (componentGroup.productVariantIds?.includes(variantId)) {
                priceModifierType = componentGroup.priceModifierType;
                priceModifierValue = componentGroup.priceModifierValue;
                break;
              }
            }

            // Calculate the final price with modifier applied.
            let originalPrice = typeof item.price.amount === 'string' ? Number(item.price.amount) : item.price.amount;
            if (typeof theSlot.overridePrice === 'number' && !theSlot.isFreeSlot) {
              originalPrice = theSlot.overridePrice;
            }
            const finalPrice = calculateFinalPriceModifier({
              priceModifierType,
              priceModifierValue,
              variantPrice: originalPrice
            });

            const addonAmountPence = Math.round(finalPrice * 100);
            return slotAcc + addonAmountPence;
          }, acc);
        } else {
          // For single selections, use existing logic.
          const item = variants.get(selected);

          // Apply override price if no item is found - used for non variant items.
          if (!item && typeof theSlot.overridePrice === 'number' && !theSlot.isFreeSlot) {
            return acc + theSlot.overridePrice * 100;
          }

          if (!item) return acc;

          // Find the component group that contains this variant to get price modifier.
          let priceModifierType = null;
          let priceModifierValue = null;

          for (const [, componentGroup] of componentGroups.entries()) {
            if (componentGroup.productVariantIds?.includes(selected)) {
              priceModifierType = componentGroup.priceModifierType;
              priceModifierValue = componentGroup.priceModifierValue;
              break;
            }
          }

          // Calculate the final price with modifier applied.
          let originalPrice = typeof item.price.amount === 'string' ? Number(item.price.amount) : item.price.amount;
          if (typeof theSlot.overridePrice === 'number' && !theSlot.isFreeSlot) {
            originalPrice = theSlot.overridePrice;
          }

          if (theSlot.isFreeSlot) {
            return acc + 0;
          }

          const finalPrice = calculateFinalPriceModifier({
            priceModifierType,
            priceModifierValue,
            variantPrice: originalPrice
          });

          const addonAmountPence = Math.round(finalPrice * 100);
          return acc + addonAmountPence;
        }
      }, 0);

    const totalPrice = baseProductPrice + slotUpsellTotalPrice;

    setCurrentTotalPrice(totalPrice / 100);
  }, [selections, product, variants, componentGroups, personalisationSlots]);

  const updateSelections = useCallback(
    async ({ slotId, itemSelected, groupLineItemPropertyKey = null, extraImages, supportingImage = null }) => {
      let productVariantId = null;
      const theSlot = personalisationSlots.get(slotId);
      const {
        stepId,
        slotType,
        lineItemPropertyKey,
        visualisationGroup,
        targetImagePlaceholder,
        positionX,
        positionY,
        scale
      } = theSlot;

      // Track the last modified group for visualiser tab switching
      if (visualisationGroup?.groupId) {
        setLastModifiedGroupId(visualisationGroup.groupId);
      }
      let theVariant = null;

      if (!theSlot) {
        throw new Error(`Slot ${slotId} not found`);
      }

      // Handle both single selections and arrays for upsells
      const isArraySelection = Array.isArray(itemSelected);
      const singleItemSelected = isArraySelection ? (itemSelected.length > 0 ? itemSelected[0] : null) : itemSelected;

      // Variable to store cropped image for IMAGE_UPLOAD slots
      let croppedImage = null;

      switch (slotType) {
        case 'COMPONENT':
          // For arrays, we need to handle each variant individually
          if (isArraySelection) {
            // For upsells with multiple selections, we don't need to validate each variant
            // as they will be handled differently in the cart
            theVariant = singleItemSelected ? variants.get(singleItemSelected) : null;
          } else {
            theVariant = itemSelected ? variants.get(itemSelected) : null;
            if (typeof theVariant === 'undefined') {
              throw new Error(`Variant ${itemSelected} not found`);
            }
          }
          break;
        case 'ALPHANUMERIC':
          break;
        case 'CHECKBOX':
          break;
        case 'IMAGE_UPLOAD':
          // Process image cropping if this is a masked image upload slot
          if (
            itemSelected instanceof File &&
            targetImagePlaceholder &&
            positionX != null &&
            positionY != null &&
            scale != null
          ) {
            try {
              // Calculate mask aspect ratio from targetImagePlaceholder dimensions
              const maskAspectRatio =
                targetImagePlaceholder?.width && targetImagePlaceholder?.height
                  ? targetImagePlaceholder.height / targetImagePlaceholder.width
                  : undefined; // Will default to 9/16 in crop function

              croppedImage = await cropMaskedImageWithPadding({
                imageFile: itemSelected,
                positioning: {
                  x: positionX,
                  y: positionY,
                  scale: scale
                },
                maskAspectRatio
                // padding defaults to 10
              });
            } catch (error) {
              // Error already logged in cropMaskedImageWithPadding
              // Continue without cropped image if cropping fails
              logWarning({
                label: 'updateSelections',
                warning: 'Image cropping failed, croppedImage will be null',
                message: 'Image cropping failed, croppedImage will be null',
                extraData: { slotId, error }
              });
            }
          }
          break;
        default:
          // @TODO: tighten up the slot type handling.
          break;
      }

      // If we're on the base product configuration slot - we want to update the product variant id.
      if (slotId === baseProductConfigurationSlotId) {
        productVariantId = singleItemSelected;
        const variant = variants.get(productVariantId);

        // Only track variant selection if we have a valid variant.
        if (variant && variant.price && variant.price.amount) {
          trackVariantSelected({
            variantId: productVariantId,
            variantPrice: Number(variant.price.amount),
            variantTitle: variant.title
          });
        }
      }

      setSelections((prev) => {
        const newState = {
          ...prev,
          ...(productVariantId && { productVariantId }),
          slots: prev.slots.map((slot) => {
            if (slot.id === slotId) {
              const updatedSlot = {
                ...slot,
                groupLineItemPropertyKey,
                lineItemPropertyKey,
                selected: itemSelected,
                extraImages:
                  extraImages !== null && extraImages !== undefined
                    ? slot.extraImages
                      ? [...slot.extraImages, extraImages]
                      : [extraImages]
                    : slot.extraImages,
                ...(supportingImage !== undefined && { supportingImage }),
                ...(theVariant && { componentGroupId: theVariant.componentGroupId }),
                // Store cropped image if available (for IMAGE_UPLOAD slots with mask)
                // The original itemSelected (masked image) is kept for the visualizer API
                // The croppedImage will be used for line item properties
                ...(croppedImage !== null && { croppedImage })
              };
              return updatedSlot;
            }
            return slot;
          })
        };
        return newState;
      });

      const stepNumber = Array.from(personalisationSteps.keys()).indexOf(stepId);
      const step = personalisationSteps.get(stepId);
      const slotPosition = step?.slots?.findIndex((id) => id === slotId);

      // Skip the base product configuration slot as it's not a real slot.
      if (itemSelected && stepId !== baseProductConfigurationStepId) {
        // For arrays, track each selection individually
        if (isArraySelection) {
          itemSelected.forEach((selectedItem) => {
            trackSlotPopulated({
              slotName: theSlot.handle,
              slotType: slotType,
              slotPosition: slotPosition,
              slotValue: selectedItem,
              stepNumber: Number(stepNumber),
              stepName: stepId
            });
          });
        } else {
          trackSlotPopulated({
            slotName: theSlot.handle,
            slotType: slotType,
            slotPosition: slotPosition,
            slotValue: itemSelected,
            stepNumber: Number(stepNumber),
            stepName: stepId
          });
        }
      }
      if (itemSelected === null && stepId !== baseProductConfigurationStepId) {
        trackSlotRemoved({
          slotName: theSlot.handle,
          slotType: slotType,
          slotPosition: slotPosition,
          stepNumber: Number(stepNumber),
          stepName: stepId
        });
      }
      if (stepId === baseProductConfigurationStepId) {
        // Reset the hidden state of the base product configuration step.
        setAllHiddenToFalse({ setPersonalisationSteps, setPersonalisationSlots, setSelections });
      }
      hideSlotSelections({ hideSlotsOnVariantsSettings, productVariantId, setSelections, setPersonalisationSlots });
    },
    [personalisationSlots, personalisationSteps, variants, hideSlotsOnVariantsSettings]
  );

  // Hide steps that don't have any slots left.
  useHideSteps({ personalisationSlots, setPersonalisationSteps, selectionsSlots: selections.slots });

  const resetJourney = useCallback(async () => {
    setIsResetting(true);

    const ids = Array.from(personalisationSteps.keys());
    if (ids.length > 0) {
      setCurrentStepId(ids[0]);
    } else {
      setCurrentStepId('');
    }

    if (
      personalisationSlots &&
      typeof personalisationSlots.values === 'function' &&
      typeof personalisationSlots.size === 'number' &&
      !Array.isArray(personalisationSlots)
    ) {
      const slotsMap = personalisationSlots;
      setSelections({
        productVariantId: product?.variants.length === 1 ? product.variants[0].id : null,
        slots: Array.from(slotsMap.values()).map(
          ({
            id,
            stepId,
            lineItemPropertyKey,
            slotType,
            slotMode,
            isRequired,
            hidden,
            visualised,
            visualisationGroup,
            translatedLineItemPropertyKey
          }) => ({
            id,
            selected: null,
            componentGroupId: null,
            stepId,
            lineItemPropertyKey,
            slotType,
            slotMode,
            isRequired,
            groupLineItemPropertyKey: null,
            hidden,
            visualised,
            visualisationGroup: visualisationGroup || null,
            translatedLineItemPropertyKey: translatedLineItemPropertyKey || null,
            extraImages: []
          })
        )
      });
    } else {
      setSelections({
        productVariantId: product?.variants.length === 1 ? product.variants[0].id : null,
        slots: []
      });
    }
    setSelectedVariantId(null);
    setCurrentSlotId(0);
    setCurrentTotalPrice(0);

    await wait({ ms: 300 });
    setIsResetting(false);
  }, [
    personalisationSteps,
    setCurrentStepId,
    setSelections,
    personalisationSlots,
    setSelectedVariantId,
    setCurrentSlotId,
    setCurrentTotalPrice,
    product
  ]);

  /**
   * Reorders the slots in selections based on a new order of slot IDs.
   * @param {object} props - The component props.
   * @param {string[]} props.newOrder - Array of slot IDs in the new order.
   */
  const reorderSlotValues = useCallback(({ newOrder }) => {
    setSelections((prev) => {
      // No re-ordering required if the new order is empty.
      if (newOrder.length === 0) {
        return prev;
      }
      // Find all component slots and their current selected values
      const firstItemFromNewOrder = newOrder.at(0);
      const lastItemFromNewOrder = newOrder.at(-1);

      // Find where the items are that are being reordered in the new order.
      const positionOfItemsBeforeFirstItem = prev.slots.findIndex(({ id }) => id === firstItemFromNewOrder);
      // +1 Because of the item just removed.
      const positionOfItemsAfterLastItem = prev.slots.findIndex(({ id }) => id === lastItemFromNewOrder) + 1;

      // Split prev.slots into three arrays - items before the first item, items between the first and last item, and items after the last item.
      const itemsBeforeFirstItem = prev.slots.slice(0, positionOfItemsBeforeFirstItem);
      const itemsBetweenFirstAndLastItem = prev.slots.slice(
        positionOfItemsBeforeFirstItem,
        positionOfItemsAfterLastItem
      );
      const itemsAfterLastItem = prev.slots.slice(positionOfItemsAfterLastItem);

      // Reorder the items between the first and last item.
      const componentSlotIds = itemsBetweenFirstAndLastItem.map(({ id }) => id);
      const selectedValues = itemsBetweenFirstAndLastItem.map(({ selected }) => selected);
      const baseProductSlot = itemsBetweenFirstAndLastItem.find(({ id }) => id === baseProductConfigurationSlotId);

      // Reorder the selected values according to newOrder
      const reorderedSelectedValues = newOrder.map((id) => {
        const idx = componentSlotIds.indexOf(id);
        return idx !== -1 ? selectedValues[`${idx}`] : null;
      });

      let reorderedIndex = 0;
      // Assign the reordered selected values back to the component slots in their original positions
      const newSlots = itemsBetweenFirstAndLastItem.map((slot) => {
        // Leave the base product slot as is - its always last.
        if (slot.id === baseProductConfigurationSlotId) {
          return {
            ...slot,
            selected: baseProductSlot?.selected ?? null
          };
        }
        return {
          ...slot,
          selected: reorderedSelectedValues[reorderedIndex++] ?? null
        };
      });

      return {
        ...prev,
        slots: [...itemsBeforeFirstItem, ...newSlots, ...itemsAfterLastItem]
      };
    });
  }, []);

  // Handle hiding slots and steps based on the variant selection.
  const { filteredPersonalisationSteps, filteredPersonalisationSlots, filteredSelections } =
    useCacheFilteredPersonalisationStepsAndSlots({ personalisationSteps, personalisationSlots, selections });

  // Handle visualisation updates and management.
  const { visualisationUrls } = useVisualisationUrlManagement({
    deps: {
      filteredSelections,
      personalisationId: personalisation?.id,
      visualiserSlotMode,
      variants
    },
    baseProductConfigurationSlotId,
    slots: Array.from(filteredPersonalisationSlots.values()),
    steps: personalisationSteps
  });

  // Extract visualisation groups from slots
  useEffect(() => {
    const groups = new Map();
    Array.from(personalisationSlots.values()).forEach((slot) => {
      if (slot.visualisationGroup) {
        groups.set(slot.visualisationGroup.groupId, {
          label: slot.visualisationGroup.label,
          templateId: slot.visualisationGroup.templateId,
          visualisationType: slot.visualisationGroup.visualisationType,
          supportingImageLineItemPropertyKey: slot.visualisationGroup.supportingImageLineItemPropertyKey
        });
      }
    });
    setVisualisationGroups(groups);
  }, [personalisationSlots]);

  const updateTheCart = useCallback(
    async ({ country, onSuccess }) => {
      if (!product) {
        throw new Error('No product selected yet');
      }
      // Check if there's a base product configuration slot, otherwise use the productVariantId
      const baseProductId =
        selections.slots.find((slot) => slot.id === baseProductConfigurationSlotId)?.selected ||
        selections.productVariantId;
      if (!baseProductId) {
        throw new Error('No base product selected yet');
      }

      setIsAddingToCart(true);
      setIsRetrying(false); // Reset retry state at start

      try {
        trackPersonalisationComplete({
          totalPrice: currentTotalPrice,
          totalSteps: personalisationSteps.size
        });

        // Build choices array using extracted utility
        const choices = buildChoicesFromSelections({ selections });

        await addToCart({
          baseProduct: {
            id: baseProductId,
            quantity: 1
          },
          slots: selections.slots,
          country,
          choices,
          visualiserImageUrls: await visualisationUrls,
          baseAttributes: selections.slots.reduce(processBaseAttributes({ variants, personalisationSlots }), {}),
          selections, // Pass selections so imagePropertiesProcessing can access croppedImage
          onRetryStart: () => setIsRetrying(true),
          onRetryEnd: () => setIsRetrying(false)
        });

        // Call success callback if provided (to close modal)
        if (onSuccess) {
          // Wait for modal closing animation to complete, then open cart drawer
          setTimeout(async () => {
            onSuccess();
            // Open cart drawer after modal closes
            // @ts-ignore - CartDrawer is added to window at runtime
            await window.CartDrawer?.open?.();
            // Reset journey after cart drawer opens
            await resetJourney();
          }, 400);
        }
      } catch (error) {
        console.error('Failed to add to cart:', error);
        throw error;
      } finally {
        setIsAddingToCart(false);
        setIsRetrying(false); // Ensure retry state is reset
      }
    },
    [
      product,
      selections,
      currentTotalPrice,
      personalisationSteps.size,
      personalisationSlots,
      variants,
      resetJourney,
      setIsAddingToCart,
      visualisationUrls
    ]
  );

  return (
    <PersonalisationContext.Provider
      value={{
        currentTotalPrice,
        setCurrentTotalPrice,
        currentStepId,
        setCurrentStepId,
        currentSlotId,
        setCurrentSlotId,
        personalisation,
        personalisationSteps: filteredPersonalisationSteps,
        personalisationSlots: filteredPersonalisationSlots,
        selectedVariantId,
        setSelectedVariantId,
        variants,
        addonGroups,
        addonItems,
        componentGroups,
        loading,
        isAddingToCart,
        isRetrying,
        setIsRetrying,
        selections: filteredSelections,
        setSelections,
        product,
        updateSelections,
        updateTheCart,
        reorderSlotValues,
        setStart,
        personalisationModalOpen,
        setPersonalisationModalOpen,
        start,
        resetJourney,
        isResetting,
        personalisationId: personalisation?.id,
        visualiserImageUrl: visualisationUrls,
        visualisationGroups,
        lastModifiedGroupId,
        hasPersonalisationOptions
      }}
    >
      {loading ? (
        <button className="button button--primary button--orange button--full-width">Loading...</button>
      ) : (
        children
      )}
    </PersonalisationContext.Provider>
  );
};

// Custom hook to use the context
export const usePersonalisationContext = () => {
  const context = useContext(PersonalisationContext);
  if (!context) {
    throw new Error('useAppContext must be used within PersonalisationProvider');
  }
  return context;
};
